<template>
  <div class="qa-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
        <h1 class="viva-page-title">VIVA QA</h1>
      </div>
    </div>

    <div class="viva-container">
      <!-- Navigation -->
      <div class="qa-nav-links" style="margin-top: 10px; margin-bottom: 20px; font-size: 20px; margin-left: 25px;">
        <span style="color: var(--viva-orange); font-weight: 600;">QA Page</span>
        <span style="margin: 0 12px; color: #918E8A;">|</span>
        <router-link to="/approval" style="color: #80C297; font-weight: 600; text-decoration: none;">Account Approval</router-link>
      </div>

      <!-- Loading and Error States -->
      <div v-if="loading" class="loading-container">
        <p>Loading QA data...</p>
      </div>

      <div v-if="error" class="error-container">
        <p class="error-message">{{ error }}</p>
        <button @click="fetchQAData" class="viva-button">Retry</button>
      </div>

      <!-- Actions Section -->
      <div v-if="!loading && !error" class="actions-section" style="display: flex; gap: 40px; align-items: flex-start; margin-bottom: 30px;">
        <!-- Search by Email -->
        <div class="search-section" style="flex: 1;">
          <h3>Search by User Email</h3>
          <div class="search-input-group">
            <input
              type="email"
              v-model="searchEmail"
              placeholder="Enter student email address"
              class="form-control search-input"
              @keyup.enter="searchByEmail"
            />
            <button @click="searchByEmail" class="btn btn-primary search-button" :disabled="searching || !searchEmail">
              <i class="glyphicon glyphicon-search"></i> {{ searching ? 'Searching...' : 'Search' }}
            </button>
          </div>
          <div v-if="searchMessage" :class="['search-message', searchMessageType]">
            {{ searchMessage }}
          </div>
        </div>

        <!-- Create Instructor - moved to Account Approval page -->
      </div>

      <!-- QA Table -->
      <div v-if="!loading && !error" class="qa-table-wrapper">
        <FilterSortTable
          title="Viva QA - Exam Reviews"
          :tableStructure="tableStructure"
          :itemheight="47"
          :interviews="qaExams"
          :initialOrderBy="'recorded_date'"
          :initialSortOrder="-1"
          @open-qa-modal="openQAModal"
        />
      </div>
    </div>

    <!-- QA Detail Modal -->
    <div v-if="showDetailModal" class="viva-modal-backdrop" @click="handleModalBackdropClick">
      <div class="viva-modal-content qa-modal-wide" @click.stop>
        <div class="viva-modal-header">
          <h2>Viva QA Review</h2>
          <button type="button" class="close" @click="closeModal()" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="viva-modal-body">
            <div>
              <!-- Student Info Section -->
              <div>
                <h4>{{ selectedExam.student_name }}</h4>
              </div>

              <!-- Video Review Section -->
              <div v-if="selectedExam.video_file_uid" class="video-player-section mt-4">
                <h5>Video Review</h5>
                <div class="qa-review-link-container">
                  <div v-if="selectedExam.qa_review_url" class="review-link-available">
                    <p>View this student's video and questions in the review page:</p>
                    <a
                      :href="selectedExam.qa_review_url"
                      target="_blank"
                      class="btn btn-primary btn-lg qa-review-button"
                    >
                      <i class="glyphicon glyphicon-play"></i>
                      Open Video Review
                      <i class="glyphicon glyphicon-new-window"></i>
                    </a>
                  </div>

                  <div v-else class="review-link-unavailable">
                    <p class="text-warning">
                      <i class="glyphicon glyphicon-warning-sign"></i>
                      Video review link not available
                    </p>
                    <p><small>Contact technical support if this issue persists</small></p>
                  </div>
                </div>
              </div>

              <div v-else class="video-player-section mt-4">
                <h5>Video Review</h5>
                <div class="no-video-message">
                  <p class="text-muted">No video file available for this VIVA.</p>
                </div>
              </div>

              <!-- Exam Information Grid -->
              <div class="info-grid">
                <div class="info-row">
                  <div class="info-label">Student Email</div>
                  <div class="info-value">
                    <a href="#" @click.prevent="copyToClipboard(selectedExam.student_email)">
                      {{ selectedExam.student_email }} <span title="Copy">📋</span>
                    </a>
                  </div>
                </div>

                <div class="info-row" v-if="selectedExam.student_phone">
                  <div class="info-label">Student Phone</div>
                  <div class="info-value">
                    <a href="#" @click.prevent="copyToClipboard(selectedExam.student_phone)">
                      {{ selectedExam.student_phone }} <span title="Copy">📋</span>
                    </a>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-label">VIVA Link</div>
                  <div class="info-value">
                    <a :href="selectedExam.viva_link" target="_blank" @click.prevent="copyToClipboard(selectedExam.viva_link)">
                      {{ selectedExam.viva_link }} <span title="Copy">📋</span>
                    </a>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-label">High School</div>
                  <div class="info-value">{{ selectedExam.high_school || 'Not specified' }}</div>
                </div>

                <div class="info-row">
                  <div class="info-label">Fee Waiver</div>
                  <div class="info-value">
                    <span v-if="selectedExam.fee_waiver_claimed">
                      <strong>Claimed</strong>
                      <span v-if="selectedExam.fee_waiver_reason"> - {{ selectedExam.fee_waiver_reason }}</span>
                    </span>
                    <span v-else>Not claimed</span>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-label">Project Title</div>
                  <div class="info-value">
                    <div class="paper-title">
                      {{ selectedExam.paper_title || 'No title provided' }}
                    </div>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-label">Project Description</div>
                  <div class="info-value">
                    <div class="student-note">
                      {{ selectedExam.project_description || 'No description provided (standard pipeline)' }}
                    </div>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-label">VIVA ID</div>
                  <div class="info-value">
                    <a href="#" @click.prevent="copyToClipboard(selectedExam.unique_id)">
                      {{ selectedExam.unique_id }} <span title="Copy">📋</span>
                    </a>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-label">Viva Type</div>
                  <div class="info-value">{{ selectedExam.viva_type || 'Not specified' }}</div>
                </div>

                <div class="info-row">
                  <div class="info-label">Recorded Date</div>
                  <div class="info-value">{{ selectedExam.recorded_date }}</div>
                </div>

                <div class="info-row">
                  <div class="info-label">Twilio Room SID</div>
                  <div class="info-value">
                    <div v-if="selectedExam.twilio_room_sid">
                      <a href="#" @click.prevent="copyToClipboard(selectedExam.twilio_room_sid)">
                        {{ selectedExam.twilio_room_sid }} <span title="Copy">📋</span>
                      </a>
                    </div>
                    <div v-else class="text-muted">
                      No Twilio room available
                    </div>
                  </div>
                </div>

                <div class="info-row" v-if="selectedExam.viva_type == 'admissions'">
                  <div class="info-label">Number of Sends</div>
                  <div class="info-value">{{ selectedExam.send_count }}</div>
                </div>

                <!-- Send Details -->
                <div class="info-row" v-if="selectedExam.sends && selectedExam.sends.length > 0">
                  <div class="info-label">Send Details</div>
                  <div class="info-value">
                    <div class="sends-list">
                      <div
                        v-for="(send, index) in selectedExam.sends"
                        :key="index"
                        class="send-item"
                      >
                        <strong>{{ send.college_name }}</strong> - {{ send.send_date }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="info-row" v-if="selectedExam.viva_type == 'assignment'">
                  <div class="info-label">Instructor</div>
                  <div class="info-value">
                    <a href="#" @click.prevent="copyToClipboard(selectedExam.instructor_email)">
                      {{ selectedExam.instructor_name }} ({{ selectedExam.instructor_email }}) <span title="Copy">📋</span>
                    </a>
                  </div>
                </div>

                <div class="info-row" v-if="selectedExam.viva_type == 'assignment'">
                  <div class="info-label">Course</div>
                  <div class="info-value">{{ selectedExam.course_title || 'No course specified' }}</div>
                </div>

                <div class="info-row" v-if="selectedExam.viva_type == 'assignment'">
                  <div class="info-label">Assignment</div>
                  <div class="info-value">{{ selectedExam.assignment_title || 'No assignment title' }}</div>
                </div>

                <div class="info-row">
                  <div class="info-label">Student Note</div>
                  <div class="info-value">
                    <div class="student-note">
                      {{ selectedExam.student_note || 'No student note provided' }}
                    </div>
                  </div>
                </div>

                <!-- Accommodation Settings -->
                <div class="info-row">
                  <div class="info-label">Accommodations</div>
                  <div class="info-value">
                    <div class="accommodation-toggles">
                      <button
                        @click="toggleTextQuestions()"
                        :disabled="selectedExam.student_status === 'ready_to_review' || selectedExam.student_status === 'completed'"
                        :class="['accommodation-btn', selectedExam.text_questions_enabled ? 'active' : '']"
                        :title="(selectedExam.student_status === 'ready_to_review' || selectedExam.student_status === 'completed') ? 'Cannot modify after student has completed Viva' : 'Toggle text questions for deaf/hard of hearing'"
                      >
                        <i class="glyphicon glyphicon-font"></i> Text Questions: {{ selectedExam.text_questions_enabled ? 'ON' : 'OFF' }}
                      </button>
                      <button
                        @click="toggleExtraTime()"
                        :disabled="selectedExam.student_status === 'ready_to_review' || selectedExam.student_status === 'completed'"
                        :class="['accommodation-btn', selectedExam.time_multiplier > 1 ? 'active' : '']"
                        :title="(selectedExam.student_status === 'ready_to_review' || selectedExam.student_status === 'completed') ? 'Cannot modify after student has completed Viva' : 'Toggle extra time (1.5x)'"
                      >
                        <i class="glyphicon glyphicon-time"></i> Extra Time: {{ selectedExam.time_multiplier > 1 ? `${selectedExam.time_multiplier}x` : 'OFF' }}
                      </button>
                    </div>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-label">QA Note</div>
                  <div class="info-value">
                    <textarea
                      class="form-control"
                      rows="3"
                      v-model="selectedExam.qa_notes"
                      @blur="() => saveQANote(false)"
                      placeholder="(editable, save on blur)"
                    ></textarea>
                  </div>
                </div>

                <!-- Timestamp Editing Section -->
                <div class="info-row timestamp-editing-section">
                  <div class="info-label">Edit Timestamps</div>
                  <div class="info-value">
                    <table class="questions-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Question Text</th>
                          <th>Timestamp (mm:ss)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(question, index) in selectedExam.questions" :key="question.question_id">
                          <td>{{ index + 1 }}</td>
                          <td>
                            <input
                              type="text"
                              v-model="question.question"
                              class="form-control"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              v-model="selectedExam.questions[index].timestamp"
                              @blur="ensureMMSSFormat(selectedExam.questions, index)"
                              class="form-control"
                              placeholder="mm:ss"
                              style="width: 100px;"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <button
                      class="btn btn-primary mt-2 mr-2"
                      @click="addNewQuestion()"
                    >
                      <i class="glyphicon glyphicon-plus"></i> Add Question
                    </button>
                    <button
                      class="btn btn-success mt-2"
                      @click="saveQuestions(selectedExam.unique_id, selectedExam.questions)"
                      :disabled="savingQuestions || !areTimestampsValid(selectedExam.questions, selectedExam.student_status)"
                    >
                      <span v-if="savingQuestions">Saving...</span>
                      <span v-else>Save Questions and Timestamps</span>
                    </button>
                    <p v-if="!areTimestampsValid(selectedExam.questions, selectedExam.student_status)" class="text-danger mt-2">
                      ⚠️ Timestamps must be in mm:ss format and in increasing order
                    </p>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="button-container mt-4">
                <!-- project Download -->
                <button v-if="selectedExam.uploaded_paper_uid" @click="downloadPaper(selectedExam.uploaded_paper_uid)" class="btn btn-info">
                  <i class="glyphicon glyphicon-download-alt"></i> Download Project
                </button>
                <button v-if="selectedExam.video_file_uid" class="btn btn-primary" @click="regenerateTranscript(false)" :disabled="transcriptGenerating">
                  <i class="glyphicon glyphicon-file"></i> {{ transcriptGenerating ? "Generating..." : "Regenerate Transcript" }}
                </button>
                <button class="btn btn-warning" @click="initiateReDo" :disabled="redoLoading">
                  <i class="glyphicon glyphicon-refresh"></i> {{ redoLoading ? "Generating..." : "Initiate Re-Do" }}
                </button>
                <button v-if="selectedExam.twilio_room_sid" class="btn btn-danger" @click="openRecoveryModal">
                  <i class="glyphicon glyphicon-flash"></i> Recover Video
                </button>
                <button class="btn btn-success" @click="toggleQAComplete">
                  {{ selectedExam.qa_complete ? "Mark QA Incomplete" : "QA Complete" }}
                </button>
              </div>
            </div>
        </div>
      </div>
    </div>

    <!-- Re-Do Questions Modal -->
    <div v-if="showRedoModal" class="viva-modal-backdrop" @click="handleRedoModalBackdropClick">
      <div class="viva-modal-content qa-modal-extra-wide" @click.stop>
        <div class="viva-modal-header">
          <h2>Review Re-Generated Questions</h2>
          <button type="button" class="close" @click="closeRedoModal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="viva-modal-body">
          <div>
            <p class="text-muted mb-3">Review the newly generated questions below. Edit as needed, then click "Approve Re-Do" to replace the current questions.</p>

            <div class="questions-comparison-container">
              <!-- Existing Questions (Left) -->
              <div class="questions-column">
                <h4 class="column-header">Existing Questions</h4>
                <div class="questions-list">
                  <div v-for="(question, index) in existingQuestions" :key="'existing-' + index" class="question-item readonly">
                    <div class="question-number">{{ index + 1 }}</div>
                    <div class="question-text">{{ typeof question === 'string' ? question : question.question }}</div>
                  </div>
                </div>
              </div>

              <!-- New Questions (Right) -->
              <div class="questions-column">
                <h4 class="column-header">New Questions (Editable)</h4>
                <div class="questions-list">
                  <div v-for="(question, index) in redoQuestions" :key="'new-' + index" class="question-item editable">
                    <div class="question-number">{{ index + 1 }}</div>
                    <textarea
                      v-model="redoQuestions[index]"
                      class="question-textarea"
                      rows="2"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <div class="button-container mt-4">
              <button class="btn btn-secondary" @click="closeRedoModal">
                Cancel
              </button>
              <button class="btn btn-warning" @click="regenerateQuestions" :disabled="redoLoading">
                <i class="glyphicon glyphicon-refresh"></i> {{ redoLoading ? "Regenerating..." : "Regenerate Questions" }}
              </button>
              <button class="btn btn-success" @click="approveAndSaveRedo" :disabled="approveLoading">
                <i class="glyphicon glyphicon-ok"></i> {{ approveLoading ? "Saving..." : "Approve Re-Do" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Viva Details Modal (for exams not ready_to_review) -->
    <div v-if="showVivaDetailsModal" class="viva-modal-backdrop" @click="() => closeModal(true)">
      <div class="viva-modal-content qa-modal-wide viva-details-modal" @click.stop>
        <div class="viva-modal-header viva-details-header">
          <h2>Viva Details</h2>
          <button type="button" class="close" @click="() => closeModal(true)" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="viva-modal-body">
          <!-- No Exam Found -->
          <div v-if="!selectedSearchExam" class="no-exam-message">
            <div class="alert alert-warning">
              <h4><i class="glyphicon glyphicon-info-sign"></i> No Viva Found</h4>
              <p>No Viva found for <strong>{{ searchEmail }}</strong>.</p>
              <p><strong>College Admissions:</strong> If this student is planning to take a Viva for college admissions but hasn't started it yet, you can create their Viva for them using the button below.</p>
              <p><strong>Course Assignments:</strong> If this student is requesting accommodation for a course assignment, the instructor will need to create that assignment before you can add accommodations to their Viva.</p>
              <button @click="createExamForUser" class="btn btn-primary btn-lg">
                <i class="glyphicon glyphicon-plus"></i> Create Viva
              </button>
            </div>
          </div>

          <!-- Exam Found -->
          <div v-if="selectedSearchExam">
            <div class="alert alert-info">
              <strong>Note:</strong> This student is preparing to take their Viva (Status: {{ selectedSearchExam.student_status }}).
              You can set accommodations now, and the recording will appear in the main table once the student completes it.
            </div>

            <!-- Student Info -->
            <div>
              <h4>{{ selectedSearchExam.student_name }}</h4>
            </div>

            <!-- Exam Information Grid -->
            <div class="info-grid">
              <div class="info-row">
                <div class="info-label">Student Email</div>
                <div class="info-value">
                  <a href="#" @click.prevent="copyToClipboard(selectedSearchExam.student_email)">
                    {{ selectedSearchExam.student_email }} <span title="Copy">📋</span>
                  </a>
                </div>
              </div>

              <div class="info-row" v-if="selectedSearchExam.student_phone">
                <div class="info-label">Student Phone</div>
                <div class="info-value">
                  <a href="#" @click.prevent="copyToClipboard(selectedSearchExam.student_phone)">
                    {{ selectedSearchExam.student_phone }} <span title="Copy">📋</span>
                  </a>
                </div>
              </div>

              <div class="info-row">
                <div class="info-label">VIVA Link</div>
                <div class="info-value">
                  <a :href="selectedSearchExam.viva_link" target="_blank" @click.prevent="copyToClipboard(selectedSearchExam.viva_link)">
                    {{ selectedSearchExam.viva_link }} <span title="Copy">📋</span>
                  </a>
                </div>
              </div>

              <div class="info-row">
                <div class="info-label">Viva ID</div>
                <div class="info-value">
                  <strong>{{ selectedSearchExam.unique_id }}</strong>
                </div>
              </div>

              <div class="info-row">
                <div class="info-label">Status</div>
                <div class="info-value">
                  <span class="badge badge-warning">{{ selectedSearchExam.student_status }}</span>
                </div>
              </div>

              <div class="info-row">
                <div class="info-label">Viva Type</div>
                <div class="info-value">{{ selectedSearchExam.viva_type || 'Not specified' }}</div>
              </div>

              <div class="info-row" v-if="selectedSearchExam.viva_type == 'assignment'">
                <div class="info-label">Instructor</div>
                <div class="info-value">
                  <a href="#" @click.prevent="copyToClipboard(selectedSearchExam.instructor_email)">
                    {{ selectedSearchExam.instructor_name }} ({{ selectedSearchExam.instructor_email }}) <span title="Copy">📋</span>
                  </a>
                </div>
              </div>

              <div class="info-row" v-if="selectedSearchExam.viva_type == 'assignment'">
                <div class="info-label">Course</div>
                <div class="info-value">{{ selectedSearchExam.course_title || 'No course specified' }}</div>
              </div>

              <div class="info-row" v-if="selectedSearchExam.viva_type == 'assignment'">
                <div class="info-label">Assignment</div>
                <div class="info-value">{{ selectedSearchExam.assignment_title || 'No assignment title' }}</div>
              </div>

              <div class="info-row">
                <div class="info-label">Project Title</div>
                <div class="info-value">
                  <div class="paper-title">
                    {{ selectedSearchExam.paper_title || 'No title provided' }}
                  </div>
                </div>
              </div>

              <!-- Accommodation Settings -->
              <div class="info-row">
                <div class="info-label">Accommodations</div>
                <div class="info-value">
                  <div class="accommodation-toggles">
                    <button
                      @click="() => toggleTextQuestions(true)"
                      :disabled="selectedSearchExam.student_status === 'ready_to_review' || selectedSearchExam.student_status === 'completed'"
                      :class="['accommodation-btn', selectedSearchExam.text_questions_enabled ? 'active' : '']"
                      :title="(selectedSearchExam.student_status === 'ready_to_review' || selectedSearchExam.student_status === 'completed') ? 'Cannot modify after student has completed Viva' : 'Toggle text questions for deaf/hard of hearing'"
                    >
                      <i class="glyphicon glyphicon-font"></i> Text Questions: {{ selectedSearchExam.text_questions_enabled ? 'ON' : 'OFF' }}
                    </button>
                    <button
                      @click="() => toggleExtraTime(true)"
                      :disabled="selectedSearchExam.student_status === 'ready_to_review' || selectedSearchExam.student_status === 'completed'"
                      :class="['accommodation-btn', selectedSearchExam.time_multiplier > 1 ? 'active' : '']"
                      :title="(selectedSearchExam.student_status === 'ready_to_review' || selectedSearchExam.student_status === 'completed') ? 'Cannot modify after student has completed Viva' : 'Toggle extra time (1.5x)'"
                    >
                      <i class="glyphicon glyphicon-time"></i> Extra Time: {{ selectedSearchExam.time_multiplier > 1 ? `${selectedSearchExam.time_multiplier}x` : 'OFF' }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="info-row">
                <div class="info-label">QA Note</div>
                <div class="info-value">
                  <textarea
                    class="form-control"
                    rows="3"
                    v-model="selectedSearchExam.qa_notes"
                    @blur="() => saveQANote(true)"
                    placeholder="(editable, save on blur)"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Results List Modal -->
    <div v-if="showSearchResultsModal" class="viva-modal-backdrop" @click="closeSearchResultsModal">
      <div class="viva-modal-content qa-modal-wide" @click.stop>
        <div class="viva-modal-header">
          <h2>Search Results for {{ searchEmail }}</h2>
          <button type="button" class="close" @click="closeSearchResultsModal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div ref="searchModalBody" class="viva-modal-body">
          <div v-if="searchResultExams.length === 0">
            <p>No Vivas found for this user.</p>
            <p><strong>College Admissions:</strong> If this student is planning to take a Viva for college admissions but hasn't started it yet, you can create their Viva for them using the button below.</p>
            <p><strong>Course Assignments:</strong> If this student is requesting accommodation for a course assignment, the instructor will need to create that assignment before you can add accommodations to their Viva.</p>
            <button @click="createExamForUser" class="btn btn-primary">
              <i class="glyphicon glyphicon-plus"></i> Create New Viva
            </button>
          </div>
          <div v-else>
            <p>Found {{ searchResultExams.length }} Viva(s):</p>
            <div ref="searchResultsList" class="search-results-list search-results-body">
              <div
                v-for="exam in searchResultExams"
                :key="exam.id"
                class="search-result-item"
                @click="openExamFromSearchResults(exam)"
              >
                <div class="result-item-main">
                  <div class="result-item-id">
                    <strong>{{ exam.unique_id }}</strong>
                  </div>
                  <div class="result-item-info">
                    <span class="badge" :class="getVivaTypeBadgeClass(exam.viva_type)">
                      {{ exam.viva_type }}
                    </span>
                    <span class="badge" :class="getStatusBadgeClass(exam.student_status)">
                      {{ exam.student_status }}
                    </span>
                  </div>
                </div>
                <div class="result-item-details">
                  <div v-if="exam.viva_type === 'assignment' && exam.course_title">
                    <strong>Course:</strong> {{ exam.course_title }}
                  </div>
                  <div v-if="exam.viva_type === 'assignment' && exam.assignment_title">
                    <strong>Assignment:</strong> {{ exam.assignment_title }}
                  </div>
                  <div v-if="exam.recorded_date">
                    <strong>Recorded:</strong> {{ exam.recorded_date }}
                  </div>
                </div>
              </div>
            </div>
            <div class="search-results-actions">
              <button @click="createExamForUser" class="btn btn-success">
                <i class="glyphicon glyphicon-plus"></i> Create New (Admissions) Viva
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recovery Modal -->
    <div v-if="showRecoveryModal" class="viva-modal-backdrop" @click="handleRecoveryModalBackdropClick">
      <div class="viva-modal-content qa-modal-extra-wide" @click.stop>
        <div class="viva-modal-header">
          <h2>Recover Video from Twilio</h2>
          <button type="button" class="close" @click="closeRecoveryModal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="viva-modal-body">
          <div class="recovery-workflow">

            <!-- Step 1: Request Composition -->
            <div class="recovery-section" :class="getStepClass(1)">
              <h4>
                <span v-if="recoveryState.compositionStatus === 'retrieved'" class="text-success">✓</span>
                1. Request Twilio Composition
              </h4>
              <p>Room SID: <strong>{{ selectedExam.twilio_room_sid }}</strong></p>
              <p>Status: <strong :class="getCompositionStatusClass()">{{ recoveryState.compositionStatus || 'Not Requested' }}</strong></p>
              <button
                class="btn btn-warning"
                @click="requestComposition"
                :disabled="recoveryState.compositionStatus !== null && recoveryState.compositionStatus !== 'not_requested' || recoveryLoading"
                :style="(recoveryState.compositionStatus !== null && recoveryState.compositionStatus !== 'not_requested' || recoveryLoading) ? 'pointer-events: none; opacity: 0.6;' : ''"
              >
                <i class="glyphicon glyphicon-refresh"></i>
                {{ recoveryLoading ? "Requesting..." : (recoveryState.compositionStatus === 'retrieved' ? "Request Composition - Completed" : "Request Composition") }}
              </button>
              <span class="text-muted ml-2"><em>(Asks Twilio to combine all video tracks into one file)</em></span>
              <p class="text-info mt-2" v-if="recoveryState.compositionStatus && recoveryState.compositionStatus !== 'not_requested' && recoveryState.compositionStatus !== 'retrieved'">
                <i class="glyphicon glyphicon-hourglass"></i> Twilio is now creating the composition. This may take a few minutes...
              </p>
              <p class="text-success mt-2" v-if="recoveryState.compositionStatus === 'retrieved'">
                ✓ Composition retrieved successfully. Proceed to Step 2 below.
              </p>
            </div>

            <!-- Step 2: Download Assets -->
            <div class="recovery-section-group">
              <h3 class="step-group-title">
                <span v-if="recoveryState.audioFiles && recoveryState.audioFiles.length > 0 && recoveryState.compositionUid" class="text-success">✓</span>
                2. Download Assets
                <span v-if="recoveryState.compositionStatus !== 'retrieved'" class="text-muted" style="font-size: 14px; font-weight: normal;"> - Complete Step 1 first</span>
              </h3>

              <!-- Step 2A: Download Composition Video -->
              <div class="recovery-section recovery-substep" :class="getStepClass('2a')">
                <h4>
                  <span v-if="recoveryState.videoDownloaded" class="text-success">✓</span>
                  2A. Download & Verify Composition Video
                  <span v-if="recoveryState.compositionStatus !== 'retrieved'" class="text-muted" style="font-size: 14px; font-weight: normal;"> - Complete Step 1 first</span>
                </h4>
              <div v-if="recoveryState.compositionUid">
                <p><strong>Instructions:</strong></p>
                <ol>
                  <li>Click the button below to download the raw video from Twilio</li>
                  <li>Open and verify the video plays correctly (has student video and audio)</li>
                  <li>After verification, check the box below to proceed to Step 2B</li>
                </ol>
                <a
                  :href="recoveryState.compositionUrl || `${env.serverAddr}/player/video/v1/file/${recoveryState.compositionUid}/`"
                  target="_blank"
                  class="btn btn-success"
                  :class="{'disabled': recoveryState.compositionStatus !== 'retrieved' || recoveryState.videoDownloaded}"
                  :style="recoveryState.videoDownloaded ? 'pointer-events: none; opacity: 0.6;' : ''"
                >
                  <i class="glyphicon glyphicon-download-alt"></i>
                  <span style="color: white;">{{ recoveryState.videoDownloaded ? 'Downloaded ✓' : 'Download Composition Video' }}</span>
                </a>
                <p class="mt-2">(Download this raw video for editing)</p>

                <div class="mt-3">
                  <label class="checkbox-inline">
                    <input type="checkbox" v-model="recoveryState.videoDownloaded" :disabled="recoveryState.compositionStatus !== 'retrieved'">
                    <strong>I've downloaded and verified the video</strong>
                  </label>
                </div>
              </div>
            </div>

            <!-- Step 2B: Generate Question Audio -->
            <div class="recovery-section recovery-substep" :class="getStepClass('2b')">
              <h4>
                <span v-if="recoveryState.audioFiles && recoveryState.audioFiles.length > 0" class="text-success">✓</span>
                2B. Generate Question Audio
                <span v-if="!recoveryState.videoDownloaded" class="text-muted" style="font-size: 14px; font-weight: normal;"> - Complete Step 2A first</span>
              </h4>
              <p><strong>Question Audio Files:</strong></p>
              <button
                class="btn btn-info"
                @click="generateAudioFiles"
                :disabled="audioGenerating || !recoveryState.videoDownloaded || (recoveryState.audioFiles && recoveryState.audioFiles.length > 0)"
                :class="{'disabled': !recoveryState.videoDownloaded || (recoveryState.audioFiles && recoveryState.audioFiles.length > 0)}"
                :style="(!recoveryState.videoDownloaded || (recoveryState.audioFiles && recoveryState.audioFiles.length > 0)) ? 'pointer-events: none; opacity: 0.6;' : ''"
              >
                <i class="glyphicon glyphicon-volume-up"></i>
                <span v-if="audioGenerating">Generating...</span>
                <span v-else-if="recoveryState.audioFiles && recoveryState.audioFiles.length > 0">Audio Generated ✓</span>
                <span v-else>Generate Question Audio</span>
              </button>
              <span class="text-muted ml-2"><em>(Generates audio from existing questions in DB)</em></span>
              <p v-if="!recoveryState.videoDownloaded && (!recoveryState.audioFiles || recoveryState.audioFiles.length === 0)" class="text-warning mt-2"><em>⚠️ Please complete Step 2A first (download and verify the video)</em></p>

              <div v-if="recoveryState.audioFiles && recoveryState.audioFiles.length > 0" class="audio-list mt-2">
                <p>Click to download individual audio files:</p>
                <ul>
                  <li v-for="(audio, index) in recoveryState.audioFiles" :key="audio.audio_uid">
                    Question {{ index + 1 }}:
                    <a :href="`${env.serverAddr}/player/video/v1/file/${audio.audio_uid}/`" target="_blank">
                      Download MP3
                    </a>
                  </li>
                </ul>
              </div>

              <p v-if="recoveryState.audioFiles && recoveryState.audioFiles.length > 0" class="mt-2">(Download these audio files for editing)</p>
              </div>
            </div>

            <!-- Step 3: Upload Edited Video -->
            <div class="recovery-section" :class="getStepClass(3)">
              <h4>
                <span v-if="recoveryState.videoAttached" class="text-success">✓</span>
                3. Upload Edited Video
                <span v-if="!recoveryState.audioFiles || recoveryState.audioFiles.length === 0" class="text-muted" style="font-size: 14px; font-weight: normal;"> - Complete Steps 1-2 first</span>
              </h4>
              <p><strong>Instructions:</strong></p>
              <ol>
                <li>Download the composition video and audio files for editing</li>
                <li>Upload the final edited video at: <a href="https://initialview.com/tools/uploader" target="_blank">https://initialview.com/tools/uploader</a></li>
                <li>After uploading, you will receive the File UID (a unique identifier like "abc123-def456-...")</li>
                <li>Enter that UID below and click "Attach Video"</li>
              </ol>
              <div class="video-upload-section">
                <label>Enter File UID from uploader:</label>
                <input
                  type="text"
                  v-model="recoveryState.videoUid"
                  class="form-control"
                  placeholder="Enter UID from uploader"
                  :disabled="!recoveryState.audioFiles || recoveryState.audioFiles.length === 0"
                />
                <button
                  class="btn btn-primary mt-2"
                  @click="attachVideo"
                  :disabled="!recoveryState.videoUid || attachingVideo || !recoveryState.audioFiles || recoveryState.audioFiles.length === 0 || recoveryState.videoAttached"
                  :style="(!recoveryState.videoUid || attachingVideo || !recoveryState.audioFiles || recoveryState.audioFiles.length === 0 || recoveryState.videoAttached) ? 'pointer-events: none; opacity: 0.6;' : ''"
                >
                  <i class="glyphicon glyphicon-link"></i>
                  <span v-if="attachingVideo">Attaching...</span>
                  <span v-else-if="recoveryState.videoAttached">Video Attached ✓</span>
                  <span v-else>Attach Video</span>
                </button>
                <span class="ml-2">(Links the edited video to this exam)</span>
                <p v-if="recoveryState.videoAttached" class="text-success mt-2">✓ Video attached successfully</p>
              </div>
            </div>

            <!-- Step 4: Edit Questions -->
            <div class="recovery-section" :class="getStepClass(4)">
              <h4>
                4. Edit Question Text and Timestamps
                <span v-if="!recoveryState.videoAttached" class="text-muted" style="font-size: 14px; font-weight: normal;"> - Complete Steps 1-3 first</span>
              </h4>
              <div v-if="recoveryState.questions && recoveryState.questions.length > 0">
                <table class="questions-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Question Text</th>
                      <th>Timestamp (mm:ss)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(question, index) in recoveryState.questions" :key="question.question_id">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <input
                          type="text"
                          v-model="question.question"
                          class="form-control"
                          :disabled="!recoveryState.videoAttached"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          v-model="recoveryState.questions[index].timestamp"
                          @blur="ensureMMSSFormat(recoveryState.questions, index)"
                          class="form-control"
                          placeholder="mm:ss"
                          style="width: 100px;"
                          :disabled="!recoveryState.videoAttached"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <button
                  class="btn btn-success mt-2"
                  @click="saveQuestions(selectedExam.unique_id, recoveryState.questions)"
                  :disabled="savingQuestions || !recoveryState.videoAttached || !areTimestampsValid(recoveryState.questions, selectedExam.student_status)"
                  :style="(savingQuestions || !recoveryState.videoAttached || !areTimestampsValid(recoveryState.questions, selectedExam.student_status)) ? 'pointer-events: none; opacity: 0.6;' : ''"
                >
                  <i class="glyphicon glyphicon-save"></i>
                  <span v-if="savingQuestions">Saving...</span>
                  <span v-else>Save Changes</span>
                </button>
                <span class="text-muted ml-2"><em>(Updates question text and timestamps in database)</em></span>
                <p v-if="!areTimestampsValid(recoveryState.questions, selectedExam.student_status)" class="text-danger mt-2">⚠️ Timestamps must be in increasing order (each question must start after the previous one)</p>
              </div>
              <div v-else>
                <p class="text-muted"><em>Questions will appear here after video is attached</em></p>
              </div>
            </div>

            <!-- Step 5: Finalize Recovery -->
            <div class="recovery-section" :class="getStepClass(5)">
              <h4>
                <span v-if="recoveryState.recoveryFinalized" class="text-success">✓</span>
                5. Finalize Recovery
                <span v-if="!recoveryState.videoAttached" class="text-muted" style="font-size: 14px; font-weight: normal;"> - Complete Steps 1-4 first</span>
              </h4>
              <div class="warning-box">
                <p class="text-danger"><strong>⚠️ WARNING ⚠️</strong></p>
                <p class="text-danger">
                  This will create or link Interview and Send objects for the student, making the Viva available for QA review.
                  This action finalizes the recovery process.
                </p>
              </div>
              <button
                class="btn btn-danger btn-lg mt-3"
                @click="confirmCompleteRecovery"
                :disabled="completingRecovery || !recoveryState.videoAttached || recoveryState.recoveryFinalized"
                :style="(completingRecovery || !recoveryState.videoAttached || recoveryState.recoveryFinalized) ? 'pointer-events: none; opacity: 0.6;' : ''"
              >
                <i class="glyphicon glyphicon-ok-circle"></i> {{ completingRecovery ? "Finalizing..." : (recoveryState.recoveryFinalized ? "Recovery Finalized ✓" : "Confirm Finalize Recovery") }}
              </button>
              <span class="text-muted ml-2"><em>(Creates or links Interview/Send objects - makes Viva available to student)</em></span>
            </div>

            <!-- Step 6: Generate AI Transcript -->
            <div class="recovery-section" :class="getStepClass(6)">
              <h4>
                <span v-if="recoveryState.transcriptStarted" class="text-success">✓</span>
                6. Generate AI Transcript
                <span v-if="!recoveryState.recoveryFinalized" class="text-muted" style="font-size: 14px; font-weight: normal;"> - Complete Step 5 first</span>
              </h4>
              <p>Click the button below to start the AI transcription process. You can check the review page later to verify the transcript was generated successfully.</p>
              <button
                class="btn btn-info mt-2"
                @click="regenerateTranscript(true)"
                :disabled="transcriptGenerating || !recoveryState.recoveryFinalized || recoveryState.transcriptStarted"
                :style="(transcriptGenerating || !recoveryState.recoveryFinalized || recoveryState.transcriptStarted) ? 'pointer-events: none; opacity: 0.6;' : ''"
              >
                <i class="glyphicon glyphicon-file"></i>
                <span v-if="transcriptGenerating">Generating...</span>
                <span v-else-if="recoveryState.transcriptStarted">Transcript Generated ✓</span>
                <span v-else>Generate Transcript</span>
              </button>
              <span class="text-muted ml-2"><em>(Triggers AI transcription pipeline)</em></span>
              <p v-if="!recoveryState.recoveryFinalized" class="text-warning mt-2"><em>⚠️ Please complete Step 5 (Finalize Recovery) first</em></p>
            </div>

          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import apiClient from "../utils/apiClient.js";
import env from "../utils/env.js";
import FilterSortTable from "../components/FilterSortTable.vue";
import { ivbus, ivAlertMixin } from "iv-base-components";

export default {
  name: "VivaQA",

  components: {
    FilterSortTable,
  },

  mixins: [ivAlertMixin],

  data() {
    return {
      env: env, // Make env available in template
      loading: true,
      error: null,
      qaExams: [],
      selectedExam: {},
      originalQANote: '',
      showDetailModal: false,
      // Search functionality
      searchEmail: '',
      searching: false,
      searchMessage: '',
      searchMessageType: '',
      selectedSearchExam: null,
      originalSearchQANote: '',
      searchResultExams: [],
      showSearchResultsModal: false,
      searchResultsScrollPosition: 0,
      showVivaDetailsModal: false,
      showRedoModal: false,
      redoLoading: false,
      approveLoading: false,
      redoQuestions: [],
      existingQuestions: [],
      generationPollingInterval: null,
      // Recovery modal state
      showRecoveryModal: false,
      recoveryLoading: false,
      audioGenerating: false,
      attachingVideo: false,
      savingQuestions: false,
      completingRecovery: false,
      transcriptGenerating: false,
      statusPollingInterval: null,
      audioPollInterval: null,   // setInterval handle for the generate_audio_status poll (Card 767/839/840 Spec 2)
      audioPollFailureCount: 0,  // consecutive network/exception failures polling audio status, reset on any successful response
      AUDIO_POLL_MAX_CONSECUTIVE_FAILURES: 5,
      recoveryState: {
        compositionStatus: null,
        compositionUid: null,
        compositionUrl: null,
        videoDownloaded: false,
        audioFiles: [],
        videoUid: '',
        videoAttached: false,
        questions: [],
        questionsSaved: false,
        recoveryFinalized: false,
        transcriptStarted: false
      },
      tableStructure: [
        {
          ref: "open-qa-modal",
          title: "",
          icon: "glyphicon glyphicon-folder-open",
          filtersort: "none",
          filter: "",
          style: "max-width:50px;min-width:50px;",
          cellType: "emitButton",
        },
        {
          ref: "student_email",
          title: "Student Email",
          filtersort: "text",
          filter: "",
          style: "max-width:200px;min-width:200px;",
        },
        {
          ref: "paper_title",
          title: "Project Title",
          filtersort: "text",
          filter: "",
          style: "max-width:250px;min-width:200px;",
          cellType: "truncatedText",
        },
        {
          ref: "recorded_date",
          title: "Recorded Date",
          filtersort: "text",
          filter: "",
          style: "max-width:150px;min-width:150px;",
        },
        {
          ref: "viva_type",
          title: "Viva Type",
          filtersort: "text",
          filter: "",
          style: "max-width:120px;min-width:120px;",
        },
        {
          ref: "text_questions_enabled",
          title: "Text Qs",
          filtersort: "dropdown",
          filter: "",
          style: "max-width:80px;min-width:80px;text-align:center;",
          cellType: "options",
          options: [
            { val: true, title: "YES" },
            { val: false, title: "NO" }
          ]
        },
        {
          ref: "time_multiplier",
          title: "Extra Time",
          filtersort: "dropdown",
          filter: "",
          style: "max-width:100px;min-width:100px;text-align:center;",
          cellType: "options",
          options: [
            { val: 1.0, title: "NO" },
            { val: 1.5, title: "1.5x" }
          ]
        },
        {
          ref: "send_count",
          title: "Sends",
          filtersort: "text",
          filter: "",
          style: "max-width:80px;min-width:80px;",
        },
        {
          ref: "student_note",
          title: "Student Note",
          filtersort: "text",
          filter: "",
          style: "max-width:200px;min-width:200px;",
          cellType: "truncatedText",
        },
        {
          ref: "qa_notes",
          title: "QA Notes",
          filtersort: "text",
          filter: "",
          style: "max-width:200px;min-width:200px;",
          cellType: "truncatedText",
        },
      ],
    };
  },

  mounted() {
    this.fetchQAData();
  },

  beforeDestroy() {
    // Clean up polling intervals
    if (this.generationPollingInterval) {
      clearInterval(this.generationPollingInterval);
    }
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
    }
    if (this.audioPollInterval) {
      clearInterval(this.audioPollInterval);
    }
  },

  methods: {
    secondsToMMSS(seconds) {
      // Converts seconds to mm:ss format (or returns mm:ss if already in that format)
      if (seconds === null || seconds === undefined) return '';

      // If it's already in mm:ss format (contains ':'), return as-is
      if (typeof seconds === 'string' && seconds.includes(':')) {
        return seconds;
      }

      // If it's an empty string, return it
      if (seconds === '') return '';

      // Convert from seconds (number or numeric string) to mm:ss
      const totalSeconds = Math.floor(Number(seconds));

      // Handle invalid numbers
      if (isNaN(totalSeconds)) {
        console.warn('Cannot convert to mm:ss, invalid number:', seconds);
        return '';
      }

      const minutes = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    mmssToSeconds(mmss) {
      // Converts mm:ss format to seconds (for validation purposes)
      if (!mmss || mmss === '') return 0;

      // Handle if it's already a number (backward compatibility)
      if (typeof mmss === 'number') return mmss;

      const parts = String(mmss).trim().split(':');
      if (parts.length !== 2) {
        console.warn('Invalid timestamp format:', mmss);
        return null;
      }

      const minutes = parseInt(parts[0], 10);
      const seconds = parseInt(parts[1], 10);

      if (isNaN(minutes) || isNaN(seconds)) {
        console.warn('Invalid numbers in timestamp:', mmss, 'minutes:', minutes, 'seconds:', seconds);
        return null;
      }

      if (seconds < 0 || seconds >= 60) {
        console.warn('Seconds out of range (0-59):', seconds);
        return null;
      }

      return minutes * 60 + seconds;
    },

    ensureMMSSFormat(questions, index) {
      // When user focuses on a timestamp field, convert it to mm:ss if it's not already
      const timestamp = questions[index].timestamp;

      // If it's a number or doesn't contain ':', convert it
      if (typeof timestamp === 'number' || (typeof timestamp === 'string' && !timestamp.includes(':'))) {
        questions[index].timestamp = this.secondsToMMSS(timestamp);
      }
    },

    areTimestampsValid(questions, student_status) {
      // Validates that timestamps are in mm:ss format and increasing order
      if (!questions || questions.length === 0 || student_status === 'ready_to_record' || student_status === 'ready_to_pay' || student_status === 'ready_to_upload') {
        return true;
      }

      for (let i = 0; i < questions.length; i++) {
        const timestamp = questions[i].timestamp;
        const timestampSeconds = this.mmssToSeconds(timestamp);

        if (timestampSeconds === null || timestampSeconds < 0) {
          console.error(`Question ${i + 1} has invalid timestamp:`, timestamp);
          return false; // Invalid if timestamp is not in valid mm:ss format
        }

        if (i > 0) {
          const prevTimestamp = questions[i - 1].timestamp;
          const prevTimestampSeconds = this.mmssToSeconds(prevTimestamp);
          if (timestampSeconds <= prevTimestampSeconds) {
            console.error(`Question ${i + 1} timestamp (${timestampSeconds}s) is not greater than previous (${prevTimestampSeconds}s)`);
            return false; // Current timestamp must be greater than previous
          }
        }
      }
      
      return true;
    },
    getStepClass(stepNumber) {
      // Returns CSS class for step based on completion state
      if (stepNumber === 1) {
        return this.recoveryState.compositionStatus === 'retrieved' ? 'step-completed' : 'step-active';
      } else if (stepNumber === '2a') {
        // Step 2A requires composition to be retrieved first
        if (this.recoveryState.compositionStatus !== 'retrieved') return 'step-disabled';
        // Step 2A is complete when user checks the "downloaded" checkbox
        return this.recoveryState.videoDownloaded ? 'step-completed' : 'step-active';
      } else if (stepNumber === '2b') {
        // Step 2B requires Step 2A to be complete (video downloaded)
        if (!this.recoveryState.videoDownloaded) return 'step-disabled';
        // Step 2B is complete when audio files have been generated
        return (this.recoveryState.audioFiles && this.recoveryState.audioFiles.length > 0) ? 'step-completed' : 'step-active';
      } else if (stepNumber === 3) {
        // Step 3 requires Step 2B to be complete (audio generated)
        if (this.recoveryState.compositionStatus !== 'retrieved' ||
            !this.recoveryState.audioFiles || this.recoveryState.audioFiles.length === 0) {
          return 'step-disabled';
        }
        return this.recoveryState.videoAttached ? 'step-completed' : 'step-active';
      } else if (stepNumber === 4) {
        if (!this.recoveryState.videoAttached) return 'step-disabled';
        return 'step-active';
      } else if (stepNumber === 5) {
        if (!this.recoveryState.videoAttached) return 'step-disabled';
        return this.recoveryState.recoveryFinalized ? 'step-completed' : 'step-active';
      } else if (stepNumber === 6) {
        // Step 6 requires Step 5 to be complete (recovery finalized)
        if (!this.recoveryState.recoveryFinalized) return 'step-disabled';
        return this.recoveryState.transcriptStarted ? 'step-completed' : 'step-active';
      }
      return '';
    },
    async fetchQAData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await apiClient.get('/api/viva/qa/data/');

        if (response.data.success) {
          this.qaExams = response.data.data;

          // Force table to recalculate column widths after data loads
          this.$nextTick(() => {
            setTimeout(() => {
              // Trigger resize event to force FilterSortTable to recalculate
              window.dispatchEvent(new Event('resize'));
            }, 100);
          });
        } else {
          this.error = response.data.error || 'Failed to load QA data';
        }
      } catch (error) {
        console.error('Error fetching QA data:', error);
        this.error = 'Failed to load QA data. Please check your connection and try again.';
      } finally {
        this.loading = false;
      }
    },

    async searchByEmail() {
      if (!this.searchEmail || !this.searchEmail.trim()) {
        return;
      }

      this.searching = true;
      this.searchMessage = '';
      this.searchMessageType = '';

      try {
        const response = await apiClient.get('/api/viva/qa/search_by_user/', {
          params: { email: this.searchEmail.trim() }
        });

        if (response.data.success) {
          if (response.data.count === 0) {
            // No exam found - show option to create
            this.searchResultExams = [];
            this.showSearchResultsModal = true;
            this.searchMessage = '';
          } else {
            // Exams found - show list of all exams
            this.searchResultExams = response.data.data;
            this.showSearchResultsModal = true;
            this.searchMessage = '';
          }
        } else {
          this.searchMessage = response.data.error || 'Search failed';
          this.searchMessageType = 'error';
        }
      } catch (error) {
        console.error('Error searching by email:', error);
        if (error.response && error.response.status === 404) {
          this.searchMessage = `User with email ${this.searchEmail} not found`;
        } else {
          this.searchMessage = 'Search failed. Please try again.';
        }
        this.searchMessageType = 'error';
      } finally {
        this.searching = false;
      }
    },

    async createExamForUser() {
      if (!this.searchEmail || !this.searchEmail.trim()) {
        return;
      }

      try {
        const response = await apiClient.post('/api/viva/qa/create_exam/', {
          email: this.searchEmail.trim()
        });

        if (response.data.success) {
          // Update the modal with the newly created exam
          this.selectedSearchExam = {
            id: response.data.id,
            unique_id: response.data.exam_id,
            student_email: response.data.student_email,
            student_name: response.data.student_name,
            student_status: response.data.student_status,
            paper_title: '',
            qa_notes: '',
            viva_type: response.data.viva_type || '',
            course_title: response.data.course_title || '',
            assignment_title: response.data.assignment_title || '',
            text_questions_enabled: false,
            time_multiplier: 1.0
          };

          // Close search results modal and open Viva Details modal
          this.showSearchResultsModal = false;
          this.showVivaDetailsModal = true;

          // Show success message
          this.showMessage(`Viva created successfully for ${response.data.student_name}`, 'alert-success', 3000);
        } else {
          this.showMessage(response.data.error || 'Failed to create exam', 'alert-danger', 5000);
        }
      } catch (error) {
        console.error('Error creating exam:', error);
        const errorMsg = error.response?.data?.error || 'Failed to create exam';
        this.showMessage(errorMsg, 'alert-danger', 5000);
      }
    },

    closeSearchResultsModal() {
      this.showSearchResultsModal = false;
      this.searchResultExams = [];
    },

    openExamFromSearchResults(exam) {

      // Save scroll position before closing search results modal
      // Try multiple possible scroll containers
      if (this.$refs.searchModalBody) {
        this.searchResultsScrollPosition = this.$refs.searchModalBody.scrollTop;
      } else if (this.$refs.searchResultsList) {
        this.searchResultsScrollPosition = this.$refs.searchResultsList.scrollTop;
      }

      // Close the search results modal
      this.showSearchResultsModal = false;

      // Check if this exam is ready_to_review (admissions) or completed (assignment) - should open QA Review modal
      // Update from Krystal - 'ready_to_record' and 'ready_to_pay' should also open QA modal, so we can edit questions before students take vivas
      if (exam.student_status === 'ready_to_review' || exam.student_status === 'completed' || exam.student_status === 'ready_to_record' || exam.student_status === 'ready_to_pay') {
        // DEFENSIVE: Set selectedSearchExam for consistency
        this.selectedSearchExam = exam;
        this.originalSearchQANote = exam.qa_notes || '';
        this.openQAModal(exam);
      } else {
        // Not ready for review - open Viva Details modal
        this.selectedSearchExam = exam;
        this.originalSearchQANote = exam.qa_notes || '';
        this.showVivaDetailsModal = true;
      }
    },

    getVivaTypeBadgeClass(type) {
      return {
        'badge-primary': type === 'assignment',
        'badge-info': type === 'admissions'
      };
    },

    getStatusBadgeClass(status) {
      return {
        'badge-success': status === 'ready_to_review' || status === 'completed',
        'badge-warning': status === 'ready_to_pay' || status === 'ready_to_record',
        'badge-default': status === 'ready_to_upload' || !status
      };
    },

    async openQAModal(exam) {
      this.selectedExam = { ...exam };
      this.originalQANote = exam.qa_notes || '';
      // Load questions for timestamp editing
      const questions = await this.loadQuestions(exam.unique_id);
      // Use $set to ensure Vue reactivity for the questions array
      this.$set(this.selectedExam, 'questions', questions);
      console.log(this.selectedExam.questions);
      console.log(this.selectedExam.questions.length);
      this.showDetailModal = true;
    },

    // openModal(modalId) {
    //   $(`#${modalId}`).modal('show');
    // },

    /**
     * Close a modal window.
     *
     * @param {boolean} isSearchContext - If true, closes the search details modal; if false, closes the main detail modal
     */
    async closeModal(isSearchContext = false) {
      // Save any pending QA notes before closing
      await this.saveQANote(isSearchContext);

      if (isSearchContext) {
        this.showVivaDetailsModal = false;
        this.selectedSearchExam = null;
        // Return to search results if they exist
        if (this.searchResultExams.length > 0) {
          this.showSearchResultsModal = true;
          // Restore scroll position after DOM updates
          this.$nextTick(() => {
            if (this.$refs.searchModalBody) {
              this.$refs.searchModalBody.scrollTop = this.searchResultsScrollPosition;
            } else if (this.$refs.searchResultsList) {
              this.$refs.searchResultsList.scrollTop = this.searchResultsScrollPosition;
            }
          });
        }
      } else {
        this.showDetailModal = false;
        this.selectedExam = {};
        // Return to search results if they exist
        if (this.searchResultExams.length > 0) {
          this.showSearchResultsModal = true;
          // Restore scroll position after DOM updates
          this.$nextTick(() => {
            if (this.$refs.searchModalBody) {
              this.$refs.searchModalBody.scrollTop = this.searchResultsScrollPosition;
            } else if (this.$refs.searchResultsList) {
              this.$refs.searchResultsList.scrollTop = this.searchResultsScrollPosition;
            }
          });
        }
      }
    },

    handleModalBackdropClick() {
      this.closeModal();
    },

    async saveQANote(isSearchContext = false) {
      const exam = isSearchContext ? this.selectedSearchExam : this.selectedExam;
      if (!exam || !exam.id) return;

      // Only save if the note has changed
      const originalNote = isSearchContext ? this.originalSearchQANote : this.originalQANote;
      const currentNote = exam.qa_notes || '';
      if (currentNote === originalNote) return;

      try {
        const response = await apiClient.post('/api/viva/qa/save_note/', {
          exam_id: exam.id,
          qa_notes: exam.qa_notes
        });

        if (response.data.success) {
          // Update the original note to reflect saved state
          if (isSearchContext) {
            this.originalSearchQANote = currentNote;
          } else {
            this.originalQANote = currentNote;
          }
          // Update the main list only when not in search context
          if (!isSearchContext) {
            const examIndex = this.qaExams.findIndex(e => e.id === exam.id);
            if (examIndex !== -1) {
              this.qaExams[examIndex].qa_notes = exam.qa_notes;
            }
          }
          this.showMessage('QA note saved', 'alert-success', 3000);
        } else {
          this.showMessage('Error saving QA note: ' + response.data.error, 'alert-danger', 3000);
        }
      } catch (error) {
        console.error('Error saving QA note:', error);
        this.showMessage('Error saving QA note: ' + error.message, 'alert-danger', 3000);
      }
    },

    /**
     * Toggle text questions accommodation for the selected exam.
     *
     * Text questions accommodation displays question text on screen for deaf/hard-of-hearing students
     * in addition to playing the audio. When enabled, students will "read" questions instead of "hear" them.
     *
     * @param {boolean} isSearchContext - If true, operates on selectedSearchExam; if false, operates on selectedExam
     *
     * Validation:
     * - Cannot modify if exam doesn't have an ID
     * - Cannot modify after student has completed the viva (status = 'ready_to_review')
     *
     * Side effects:
     * - Updates the exam's text_questions_enabled property in the modal
     * - If not search context, updates the corresponding exam in the qaExams main table array
     * - Shows success/error toast message
     *
     * @async
     * @returns {Promise<void>}
     */
    async toggleTextQuestions(isSearchContext = false) {
      // DEFENSIVE: Fallback to selectedSearchExam if selectedExam is null
      // This provides redundancy in case selectedExam doesn't get set properly when modal opens from search.
      // Under normal operation, selectedExam should always have data, but this prevents errors if something breaks.
      const exam = isSearchContext ? this.selectedSearchExam : (this.selectedExam || this.selectedSearchExam);
      console.log('toggleTextQuestions called, exam:', exam);
      if (!exam || !exam.id) {
        console.error('Cannot toggle text questions: exam or exam.id is missing', exam);
        this.showMessage('Error: VIVA data is missing. Please refresh and try again.', 'alert-danger', 5000);
        return;
      }
      if (exam.student_status === 'ready_to_review' || exam.student_status === 'completed') {
        this.showMessage('Cannot modify accommodations after student has completed Viva', 'alert-warning', 3000);
        return;
      }

      try {
        const newValue = !exam.text_questions_enabled;
        const response = await apiClient.post('/api/viva/qa/update_accommodations/', {
          exam_id: exam.id,
          text_questions_enabled: newValue
        });

        if (response.data.success) {
          exam.text_questions_enabled = newValue;
          // Update the main list only when not in search context
          if (!isSearchContext) {
            const examIndex = this.qaExams.findIndex(e => e.id === exam.id);
            if (examIndex !== -1) {
              this.qaExams[examIndex].text_questions_enabled = newValue;
            }
          }
          this.showMessage(`Text questions ${newValue ? 'enabled' : 'disabled'}`, 'alert-success', 3000);
        } else {
          this.showMessage('Error updating accommodation: ' + response.data.error, 'alert-danger', 3000);
        }
      } catch (error) {
        console.error('Error toggling text questions:', error);
        this.showMessage('Error updating accommodation: ' + error.message, 'alert-danger', 3000);
      }
    },

    /**
     * Toggle extra time accommodation for the selected exam.
     *
     * Extra time accommodation provides 1.5x time multiplier for students who need additional
     * time to respond. Standard time is 60 seconds per question; with extra time it becomes
     * 90 seconds per question.
     *
     * @param {boolean} isSearchContext - If true, operates on selectedSearchExam; if false, operates on selectedExam
     *
     * Toggle behavior:
     * - If time_multiplier > 1: Set to 1.0 (disable, standard 60 seconds)
     * - If time_multiplier <= 1: Set to 1.5 (enable, extended 90 seconds)
     *
     * Validation:
     * - Cannot modify if exam doesn't have an ID
     * - Cannot modify after student has completed the viva (status = 'ready_to_review')
     *
     * Side effects:
     * - Updates the exam's time_multiplier property in the modal
     * - If not search context, updates the corresponding exam in the qaExams main table array
     * - Shows success/error toast message
     *
     * @async
     * @returns {Promise<void>}
     */
    async toggleExtraTime(isSearchContext = false) {
      // DEFENSIVE: Fallback to selectedSearchExam if selectedExam is null
      // This provides redundancy in case selectedExam doesn't get set properly when modal opens from search.
      // Under normal operation, selectedExam should always have data, but this prevents errors if something breaks.
      const exam = isSearchContext ? this.selectedSearchExam : (this.selectedExam || this.selectedSearchExam);
      console.log('toggleExtraTime called, exam:', exam);
      if (!exam || !exam.id) {
        console.error('Cannot toggle extra time: exam or exam.id is missing', exam);
        this.showMessage('Error: VIVA data is missing. Please refresh and try again.', 'alert-danger', 5000);
        return;
      }
      if (exam.student_status === 'ready_to_review' || exam.student_status === 'completed') {
        this.showMessage('Cannot modify accommodations after student has completed Viva', 'alert-warning', 3000);
        return;
      }

      try {
        // Toggle between 1.0 (no extra time) and 1.5 (time and a half)
        const newValue = exam.time_multiplier > 1 ? 1.0 : 1.5;
        const response = await apiClient.post('/api/viva/qa/update_accommodations/', {
          exam_id: exam.id,
          time_multiplier: newValue
        });

        if (response.data.success) {
          exam.time_multiplier = newValue;
          // Update the main list only when not in search context
          if (!isSearchContext) {
            const examIndex = this.qaExams.findIndex(e => e.id === exam.id);
            if (examIndex !== -1) {
              this.qaExams[examIndex].time_multiplier = newValue;
            }
          }
          this.showMessage(`Extra time ${newValue > 1 ? 'enabled (1.5x)' : 'disabled'}`, 'alert-success', 3000);
        } else {
          this.showMessage('Error updating accommodation: ' + response.data.error, 'alert-danger', 3000);
        }
      } catch (error) {
        console.error('Error toggling extra time:', error);
        this.showMessage('Error updating accommodation: ' + error.message, 'alert-danger', 3000);
      }
    },

    async toggleQAComplete() {
      if (!this.selectedExam.id) return;

      try {
        const response = await apiClient.post('/api/viva/qa/toggle_complete/', {
          exam_id: this.selectedExam.id
        });

        if (response.data.success) {
          if (response.data.qa_complete) {
            // If marked complete, remove from list and close modal
            this.qaExams = this.qaExams.filter(e => e.id !== this.selectedExam.id);
            this.closeModal();
            this.showMessage('QA marked complete', 'alert-success', 3000);
          } else {
            this.selectedExam.qa_complete = response.data.qa_complete;
            this.showMessage('QA marked incomplete', 'alert-success', 3000);
          }
        } else {
          this.showMessage('Error toggling QA status: ' + response.data.error, 'alert-danger', 3000);
        }
      } catch (error) {
        console.error('Error toggling QA status:', error);
        this.showMessage('Error toggling QA status: ' + error.message, 'alert-danger', 3000);
      }
    },

    downloadPaper(paperUid) {
      const downloadUrl = `${env.serverAddr}/shield/mercury/file/${paperUid}/`;
      window.open(downloadUrl, '_blank');
    },

    copyToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            this.showMessage("Copied to clipboard", "alert-success", 3000);
          })
          .catch(() => {
            this.showMessage("Failed to copy to clipboard", "alert-danger", 3000);
          });
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand("copy");
          this.showMessage("Copied to clipboard", "alert-success", 3000);
        } catch (err) {
          this.showMessage("Failed to copy to clipboard", "alert-danger", 3000);
        }
        document.body.removeChild(textArea);
      }
    },

    getGeneratorNumQuestions() {
      // Use existing question count if available, otherwise use defaults
      if (this.existingQuestions && this.existingQuestions.length > 0) {
        return this.existingQuestions.length - 1; // Exclude standard closing question
      } else {
        // Fallback defaults: 3 for assignments, 6 for admissions
        if (this.selectedExam && this.selectedExam.viva_type === "assignment") {
          return 3;
        } else {
          return 6;
        }
      }
    },

    async initiateReDo() {
      if (!this.selectedExam.unique_id) return;

      this.redoLoading = true;

      try {
        // Fetch existing questions
        const existingQuestionsResponse = await apiClient.get(`/api/viva/get_questions/${this.selectedExam.unique_id}/`);

        if (existingQuestionsResponse.data.status === 'success') {
          // Store all existing questions including the standard closing question
          this.existingQuestions = existingQuestionsResponse.data.questions;
        } else {
          this.existingQuestions = [];
        }

        // Generate new questions
        const response = await apiClient.post('/api/viva/generate_questions/', {
          viva_exam_id: this.selectedExam.unique_id,
          redo: 'true',
          num_questions: this.getGeneratorNumQuestions(),
        });

        // Handle async response (202 Accepted)
        if (response.status === 202 || response.data.status === 'pending') {
          // Start polling for generation status
          this.startGenerationPolling(this.selectedExam.unique_id);
          return; // Keep redoLoading true while polling
        }

        if (response.data.status === 'success') {
          // Keep all questions including the standard closing question
          this.redoQuestions = response.data.questions;
          this.showRedoModal = true;
        } else {
          // Display user-friendly error message (backend already provides standardized message)
          const errorMsg = response.data.message || 'Unable to generate questions. Please try again.';
          this.showMessage(errorMsg, 'alert-danger', 10000);
        }
      } catch (error) {
        console.error('Error generating redo questions:', error);
        this.showMessage('Unable to generate questions. Please try again.', 'alert-danger', 10000);
      } finally {
        if (!this.generationPollingInterval) {
          this.redoLoading = false;
        }
      }
    },

    closeRedoModal() {
      this.showRedoModal = false;
      this.redoQuestions = [];
      this.existingQuestions = [];
      this.stopGenerationPolling();
      this.redoLoading = false;
    },

    handleRedoModalBackdropClick() {
      this.closeRedoModal();
    },

    async regenerateQuestions() {
      if (!this.selectedExam.unique_id) return;

      this.redoLoading = true;

      try {
        // Generate new questions again
        const response = await apiClient.post('/api/viva/generate_questions/', {
          viva_exam_id: this.selectedExam.unique_id,
          redo: 'true',
          num_questions: this.getGeneratorNumQuestions(),
        });

        // Handle async response (202 Accepted)
        if (response.status === 202 || response.data.status === 'pending') {
          // Start polling for generation status
          this.startGenerationPolling(this.selectedExam.unique_id);
          return; // Keep redoLoading true while polling
        }

        if (response.data.status === 'success') {
          // Replace the questions with newly generated ones (including closing question)
          this.redoQuestions = response.data.questions;
          this.showMessage('Questions regenerated successfully', 'alert-success', 3000);
        } else {
          // Display user-friendly error message (backend already provides standardized message)
          const errorMsg = response.data.message || 'Unable to regenerate questions. Please try again.';
          this.showMessage(errorMsg, 'alert-danger', 10000);
        }
      } catch (error) {
        console.error('Error regenerating questions:', error);
        this.showMessage('Unable to regenerate questions. Please try again.', 'alert-danger', 10000);
      } finally {
        if (!this.generationPollingInterval) {
          this.redoLoading = false;
        }
      }
    },

    startGenerationPolling(vivaExamId) {
      // Clear any existing polling
      this.stopGenerationPolling();

      // Poll every 2 seconds
      this.generationPollingInterval = setInterval(async () => {
        try {
          const response = await apiClient.get(`/api/viva/generation_status/${vivaExamId}/`);

          if (response.data.status === 'done') {
            // Generation complete - show questions
            this.stopGenerationPolling();
            this.redoQuestions = response.data.questions;
            this.showRedoModal = true;
            this.redoLoading = false;
            this.showMessage('Questions generated successfully', 'alert-success', 3000);
          } else if (response.data.status === 'error') {
            // Generation failed
            console.error('Error generating redo questions:', response.data.message);
            this.stopGenerationPolling();
            this.redoLoading = false;
            const errorMsg = response.data.message || 'Unable to generate questions. Please try again.';
            this.showMessage(errorMsg, 'alert-danger', 10000);
          }
          // If status is 'pending' or 'processing', continue polling
        } catch (error) {
          console.error('Error polling generation status:', error);
          this.stopGenerationPolling();
          this.redoLoading = false;
          this.showMessage('Unable to check generation status. Please try again.', 'alert-danger', 10000);
        }
      }, 2000);
    },

    stopGenerationPolling() {
      if (this.generationPollingInterval) {
        clearInterval(this.generationPollingInterval);
        this.generationPollingInterval = null;
      }
    },

    async approveAndSaveRedo() {
      if (!this.selectedExam.id || !this.redoQuestions.length) return;

      this.approveLoading = true;

      try {
        const response = await apiClient.post('/api/viva/approve_redo_questions/', {
          viva_exam_id: this.selectedExam.id,
          questions: this.redoQuestions
        });

        if (response.data.status === 'success') {
          this.showMessage('Questions approved and saved successfully. Student status changed to Ready to Record.', 'alert-success', 5000);
          const selectedExamId = this.selectedExam.id;
          const selectedExamUniqueId = this.selectedExam.unique_id;
          this.closeRedoModal();

          // Load fresh questions immediately (before fetchQAData, since exam may leave the QA list)
          const questions = await this.loadQuestions(selectedExamUniqueId);
          this.$set(this.selectedExam, 'questions', questions);
          // Update status to reflect the backend change
          this.$set(this.selectedExam, 'student_status', 'ready_to_record');

          // Refresh the QA data list (exam will likely be removed since status is now ready_to_record)
          await this.fetchQAData();
          // Check if exam is still in the list (it won't be if status changed)
          const updatedExam = this.qaExams.find(e => e.id === selectedExamId);
          if (updatedExam) {
            // Exam still in list - merge the loaded questions
            this.selectedExam = { ...updatedExam, questions };
          }
          // If not found, selectedExam keeps its current state with updated questions
        } else {
          // Display user-friendly error message (backend already provides standardized message)
          const errorMsg = response.data.message || 'Unable to save questions. Please try again.';
          this.showMessage(errorMsg, 'alert-danger', 10000);
        }
      } catch (error) {
        console.error('Error approving redo questions:', error);
        this.showMessage('Unable to save questions. Please try again.', 'alert-danger', 10000);
      } finally {
        this.approveLoading = false;
      }
    },

    // Recovery Modal Methods
    async openRecoveryModal() {
      // Load recovery state from selectedExam
      this.recoveryState = {
        compositionStatus: this.selectedExam.composition_status || null,
        compositionUid: this.selectedExam.composition_uid || null,
        compositionUrl: this.selectedExam.composition_url || null,
        videoDownloaded: false,
        audioFiles: [],
        videoUid: '',
        videoAttached: false,
        questions: [],
        questionsSaved: false,
        recoveryFinalized: false,
        transcriptStarted: false
      };

      // Load questions for editing
      this.recoveryState.questions = await this.loadQuestions(this.selectedExam.unique_id);

      this.showRecoveryModal = true;

      // If composition is already in progress, start polling
      if (this.recoveryState.compositionStatus &&
          this.recoveryState.compositionStatus !== 'not_requested' &&
          this.recoveryState.compositionStatus !== 'retrieved') {
        this.startStatusPolling();
      }
    },

    closeRecoveryModal() {
      // Stop any active polling
      this.stopStatusPolling();

      this.showRecoveryModal = false;
      this.recoveryState = {
        compositionStatus: null,
        compositionUid: null,
        audioFiles: [],
        videoUid: '',
        videoAttached: false,
        questions: [],
        recoveryFinalized: false,
        transcriptStarted: false
      };
    },

    handleRecoveryModalBackdropClick() {
      this.closeRecoveryModal();
    },

    getCompositionStatusClass() {
      const status = this.recoveryState.compositionStatus;
      if (status === 'retrieved') return 'text-success';
      if (status === 'requested') return 'text-warning';
      if (status === 'failed') return 'text-danger';
      return 'text-muted';
    },

    async loadQuestions(examUniqueId) {
      try {
        const response = await apiClient.get(`/api/viva/get_questions/${examUniqueId}/`);

        if (response.data.status === 'success') {
          console.log('Loaded questions for exam', examUniqueId, response.data.questions);
          // Use the full question data from the API
          return response.data.questions;
        }
      } catch (error) {
        console.error('Error loading questions:', error);
        return [];
      }
    },

    async requestComposition() {
      this.recoveryLoading = true;

      try {
        const response = await apiClient.post('/api/viva/recovery/request_composition/', {
          room_sid: this.selectedExam.twilio_room_sid
        });

        if (response.data.status === 'success') {
          this.showMessage('Composition requested successfully. Waiting for Twilio to complete...', 'alert-success', 5000);
          this.recoveryState.compositionStatus = 'requested';

          // Do an immediate status check (non-blocking) to show initial state in console
          this.checkCompositionStatus();

          // Start auto-polling the database for status updates
          this.startStatusPolling();
        } else {
          this.showMessage('Error: ' + response.data.message, 'alert-danger', 5000);
        }
      } catch (error) {
        console.error('Error requesting composition:', error);
        this.showMessage('Error requesting composition: ' + error.message, 'alert-danger', 5000);
      } finally {
        this.recoveryLoading = false;
      }
    },

    startStatusPolling() {
      // Clear any existing interval
      if (this.statusPollingInterval) {
        clearInterval(this.statusPollingInterval);
      }

      // Poll every 20 seconds while composition is in progress
      this.statusPollingInterval = setInterval(() => {
        if (this.recoveryState.compositionStatus === 'requested' ||
            (this.recoveryState.compositionStatus &&
             this.recoveryState.compositionStatus !== 'not_requested' &&
             this.recoveryState.compositionStatus !== 'retrieved')) {
          this.checkCompositionStatus();
        } else {
          // Stop polling if composition is retrieved or not requested
          this.stopStatusPolling();
        }
      }, 20000); // Check every 20 seconds
    },

    stopStatusPolling() {
      if (this.statusPollingInterval) {
        clearInterval(this.statusPollingInterval);
        this.statusPollingInterval = null;
      }
    },

    async checkCompositionStatus() {
      try {
        // Refetch the QA data to get updated composition status (called internally by polling)
        const response = await apiClient.get('/api/viva/qa/data/');

        if (response.data.success) {
          const updatedExam = response.data.data.find(e => e.id === this.selectedExam.id);

          if (updatedExam) {
            this.selectedExam.composition_status = updatedExam.composition_status;
            this.selectedExam.composition_uid = updatedExam.composition_uid;
            this.recoveryState.compositionStatus = updatedExam.composition_status;
            this.recoveryState.compositionUid = updatedExam.composition_uid;

            // Only show success message when composition is ready
            if (updatedExam.composition_status === 'retrieved') {
              this.showMessage('Composition is ready!', 'alert-success', 3000);
              this.stopStatusPolling(); // Stop polling once retrieved
            }
          }
        }
      } catch (error) {
        console.error('Error checking composition status:', error);
        // Silently fail - polling will retry in 20 seconds
      }
    },

    // Card 767/839/840 Spec 2: generate_viva_question_audio now dispatches a background
    // job instead of generating audio inline, so this only kicks off the job and hands
    // off to startAudioPolling() for the result - mirrors startGenerationPolling()'s
    // dispatch-then-poll shape just above. Per
    // `.agents/skills/frontend-api-integration.md`'s no-retry-into-the-main-action-function
    // rule, the poller below calls the dedicated status endpoint directly rather than
    // re-calling generateAudioFiles() itself.
    async generateAudioFiles() {
      this.audioGenerating = true;

      try {
        const response = await apiClient.post('/api/viva/recovery/generate_audio/', {
          viva_exam_id: this.selectedExam.unique_id
        });

        if (response.data.status === 'generating_audio') {
          this.startAudioPolling(this.selectedExam.unique_id);
          return; // keep audioGenerating true while polling
        } else if (response.data.status === 'failed') {
          this.audioGenerating = false;
          this.showMessage(response.data.message || 'Audio generation failed.', 'alert-danger', 10000);
        } else {
          this.audioGenerating = false;
          this.showMessage('Error: ' + response.data.message, 'alert-danger', 5000);
        }
      } catch (error) {
        console.error('Error generating audio:', error);
        this.audioGenerating = false;
        this.showMessage('Error generating audio: ' + error.message, 'alert-danger', 5000);
      }
    },

    startAudioPolling(vivaExamId) {
      this.stopAudioPolling();
      this.audioPollFailureCount = 0;

      // Poll every 2 seconds, matching startGenerationPolling()'s cadence just above.
      this.audioPollInterval = setInterval(async () => {
        try {
          const response = await apiClient.get(`/api/viva/recovery/generate_audio_status/${vivaExamId}/`);
          this.audioPollFailureCount = 0;

          if (response.data.status === 'success') {
            this.stopAudioPolling();
            this.audioGenerating = false;
            this.recoveryState.audioFiles = response.data.audio_files;
            this.showMessage('Audio files generated successfully', 'alert-success', 3000);
          } else if (response.data.status === 'failed') {
            this.stopAudioPolling();
            this.audioGenerating = false;
            this.showMessage(response.data.message || 'Audio generation failed.', 'alert-danger', 10000);
          }
          // else: still 'generating_audio' - keep polling. No cap on this branch; a
          // genuinely stuck job is caught by the backend's own staleness check
          // (AUDIO_JOB_STALE_MINUTES), which only fires once a request actually reaches
          // it - see the catch block below for when it never does.
        } catch (error) {
          console.error('Error polling audio generation status:', error);
          // A transient blip shouldn't kill the poll, but a persistent one (offline,
          // server unreachable) would otherwise retry forever with no error ever shown,
          // since the backend staleness check never gets a chance to run. Cap consecutive
          // failures and surface an error once the cap is hit.
          this.audioPollFailureCount++;
          if (this.audioPollFailureCount >= this.AUDIO_POLL_MAX_CONSECUTIVE_FAILURES) {
            this.stopAudioPolling();
            this.audioGenerating = false;
            this.showMessage('Unable to reach the server. Please check your connection and try again.', 'alert-danger', 10000);
          }
        }
      }, 2000);
    },

    stopAudioPolling() {
      this.audioPollFailureCount = 0;
      if (this.audioPollInterval) {
        clearInterval(this.audioPollInterval);
        this.audioPollInterval = null;
      }
    },

    async attachVideo() {
      this.attachingVideo = true;

      try {
        const response = await apiClient.post('/api/viva/recovery/attach_video/', {
          viva_exam_id: this.selectedExam.unique_id,
          video_uid: this.recoveryState.videoUid
        });

        if (response.data.status === 'success') {
          this.recoveryState.videoAttached = true;
          this.showMessage('Video attached successfully', 'alert-success', 3000);
        } else {
          this.showMessage('Error: ' + response.data.message, 'alert-danger', 5000);
        }
      } catch (error) {
        console.error('Error attaching video:', error);
        this.showMessage('Error attaching video: ' + error.message, 'alert-danger', 5000);
      } finally {
        this.attachingVideo = false;
      }
    },

    addNewQuestion() {
      console.log('Adding new question');
      // Find the highest sort_order
      const maxSortOrder = this.selectedExam.questions.length > 0
        ? Math.max(...this.selectedExam.questions.map(q => q.sort_order || 0))
        : 0;

      // Add new question object without question_id (backend will create it)
      this.selectedExam.questions.push({
        question_id: null,  // No ID means backend will create new question
        question: '',
        timestamp: '',
        sort_order: maxSortOrder + 1
      });
      console.log(this.selectedExam.questions);
    },

    async saveQuestions(examUniqueId, questions) {
      this.savingQuestions = true;

      try {
        const response = await apiClient.post('/api/viva/recovery/update_questions/', {
          viva_exam_id: examUniqueId,
          questions: questions
        });

        if (response.data.status === 'success') {
          this.showMessage('Question text and timestamps saved successfully', 'alert-success', 3000);
          // Reload questions to get the new question_id for any newly created questions
          this.selectedExam.questions = await this.loadQuestions(examUniqueId);
        } else {
          this.showMessage('Error: ' + response.data.message, 'alert-danger', 5000);
        }
      } catch (error) {
        console.error('Error saving questions:', error);
        this.showMessage('Error saving questions: ' + error.message, 'alert-danger', 5000);
      } finally {
        this.savingQuestions = false;
      }
    },

    async confirmCompleteRecovery() {
      if (!confirm('Are you sure you want to complete recovery? This will create or link Interview and Send objects.')) {
        return;
      }

      this.completingRecovery = true;

      try {
        const response = await apiClient.post('/api/viva/recovery/complete/', {
          viva_exam_id: this.selectedExam.unique_id
        });

        if (response.data.status === 'success') {
          this.recoveryState.recoveryFinalized = true;
          this.showMessage('Recovery completed successfully!', 'alert-success', 5000);
          // Don't close modal - allow them to proceed to Step 6 (Generate Transcript)
          await this.fetchQAData(); // Refresh the list
        } else {
          this.showMessage('Error: ' + response.data.message, 'alert-danger', 5000);
        }
      } catch (error) {
        console.error('Error completing recovery:', error);
        this.showMessage('Error completing recovery: ' + error.message, 'alert-danger', 5000);
      } finally {
        this.completingRecovery = false;
      }
    },

    async regenerateTranscript(inRecoveryModal = false) {
      if (!this.selectedExam.unique_id) return;

      this.transcriptGenerating = true;

      try {
        const response = await apiClient.post('/api/viva/regenerate_transcript/', {
          viva_exam_id: this.selectedExam.unique_id
        });

        if (response.data.status === 'success') {
          if (inRecoveryModal) {
            this.recoveryState.transcriptStarted = true;
          }
          this.showMessage('Transcription job started - this may take 30-60 minutes to complete', 'alert-success', 8000);
        } else {
          this.showMessage('Error: ' + response.data.message, 'alert-danger', 5000);
        }
      } catch (error) {
        console.error('Error generating transcript:', error);
        this.showMessage('Error generating transcript: ' + (error.response?.data?.message || error.message), 'alert-danger', 5000);
      } finally {
        this.transcriptGenerating = false;
      }
    },

  },

};
</script>

<style scoped>
@import '../styles/viva-classes.css';

/* Override viva-container for QA page - need block layout, not flex centering */
.viva-container {
  display: block !important;
  align-items: unset !important;
  justify-content: unset !important;
}

.qa-nav-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.qa-nav-pill {
  padding: 10px 24px;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--viva-white);
  color: var(--viva-dark);
  border: 2px solid var(--viva-green);
}

.qa-nav-pill:hover {
  background: var(--viva-green);
  color: var(--viva-white);
  text-decoration: none;
}

.qa-nav-pill-active {
  background: var(--viva-green);
  color: var(--viva-white);
  cursor: default;
}

.qa-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
}

.loading-container,
.error-container {
  text-align: center;
  padding: 40px;
}

.error-message {
  color: #dc3545;
  margin-bottom: 20px;
}

.qa-table-wrapper {
  margin: 20px;
}

/* Fix FilterSortTable height to account for search section */
.qa-table-wrapper >>> #list-view {
  height: calc(100vh - 580px) !important;
}

/* QA Modal Customizations */
.qa-modal-wide {
  max-width: 900px;
}

/* Ensure modal content stays interactive */
.viva-modal-content {
  position: relative;
  z-index: 2;
}

.viva-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.viva-modal-header .close {
  background: none;
  border: none;
  font-size: 24px;
  font-weight: bold;
  color: white;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  position: absolute;
  top: 15px;
  right: 20px;
}

.viva-modal-header .close:hover {
  color: #f0f0f0;
}

.paper-section {
  margin-top: 15px;
}

.text-muted {
  opacity: 0.6;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-info {
  background-color: #17a2b8;
  color: white;
  border-color: #17a2b8;
}

.btn-success {
  background-color: #28a745;
  color: white;
  border-color: #28a745;
}

.btn:hover {
  opacity: 0.9;
}

.modal-body > div {
  margin-bottom: 10px;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  min-height: 24px;
}

.info-label {
  width: 180px;
  font-weight: bold;
  flex-shrink: 0;
  padding-right: 15px;
  color: #333;
}

.info-value {
  flex: 1;
  word-break: break-word;
}

.info-value textarea {
  width: 100%;
  resize: vertical;
}

.student-note {
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  min-height: 40px;
  font-style: italic;
}

.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

a {
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
}

a:hover {
  color: #0056b3;
}

/* Video Player Section */
.video-player-section {
  border-top: 1px solid #e5e5e5;
  padding-top: 20px;
}

.video-player-section h5 {
  margin-bottom: 15px;
  color: #333;
  font-weight: bold;
}

.video-player-container {
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  /* Fix for Video.js event capture interference */
  position: relative;
  z-index: 1;
  /* isolation: isolate; */ /* Commented out - was interfering with Video.js DOM */
}

/* Constrain video.js event capture to video area only */
.video-player-container .video-js {
  position: relative !important;
  z-index: 1 !important;
}

.video-player-container .video-js * {
  z-index: auto !important;
}

/* Constrain Video.js control elements */
.video-player-container .vjs-control-bar,
.video-player-container .vjs-big-play-button,
.video-player-container .vjs-loading-spinner {
  position: relative !important;
  z-index: 1 !important;
}

.no-video-message {
  background-color: #f8f9fa;
  padding: 40px 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  text-align: center;
}

.mt-4 {
  margin-top: 1.5rem;
}

.sends-list {
  max-height: 150px;
  overflow-y: auto;
}

.send-item {
  padding: 8px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin-bottom: 4px;
  background-color: #f8f9fa;
  font-size: 14px;
}

.send-item:last-child {
  margin-bottom: 0;
}

/* QA Review Link Styles */
.qa-review-link-container {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  text-align: center;
}

.qa-review-button {
  font-size: 16px;
  padding: 12px 24px;
  margin: 10px 0;
  background-color: var(--viva-green);
  border-color: var(--viva-green);
  color: white;
  text-decoration: none;
  display: inline-block;
  border-radius: 6px;
}

.qa-review-button:hover {
  background-color: #5a9f5f;
  border-color: #5a9f5f;
  color: white;
  text-decoration: none;
}

.review-link-note {
  margin-top: 10px;
  color: #666;
}

.review-link-unavailable {
  color: #856404;
  background-color: #fff3cd;
  padding: 15px;
  border-radius: 4px;
  border: 1px solid #ffeaa7;
}

/* Project Title Styles */
.paper-title {
  font-weight: 500;
  color: var(--viva-dark);
  line-height: 1.4;
  max-width: 100%;
  word-wrap: break-word;
}

.info-row .paper-title {
  font-style: italic;
}

/* Re-Do Questions Modal Styles */
.qa-modal-extra-wide {
  max-width: 1400px;
}

.questions-comparison-container {
  display: flex;
  gap: 20px;
  margin: 20px 0;
}

.questions-column {
  flex: 1;
  min-width: 0; /* Allows flex items to shrink below content size */
}

.column-header {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--viva-green);
  color: var(--viva-dark);
  font-size: 18px;
  font-weight: bold;
}

.questions-list {
  max-height: 500px;
  overflow-y: auto;
  margin: 0;
  padding-right: 5px;
}

.question-item {
  display: flex;
  gap: 15px;
  padding: 15px;
  margin-bottom: 12px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  align-items: flex-start;
}

.question-item.readonly {
  background-color: #f0f0f0;
  opacity: 0.9;
}

.question-item.editable {
  background-color: #fff;
  border: 1px solid var(--viva-green);
  padding: 8px;
  margin-bottom: 8px;
}

.question-number {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  background-color: var(--viva-green);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-top: 5px;
}

.question-text {
  flex: 1;
  line-height: 1.5;
  color: #333;
}

.question-textarea {
  flex: 1;
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 60px;
}

.question-textarea:focus {
  outline: none;
  border-color: var(--viva-green);
  box-shadow: 0 0 0 2px rgba(106, 168, 110, 0.2);
}

.btn-warning {
  background-color: #ffc107;
  color: #212529;
  border-color: #ffc107;
}

.btn-warning:hover {
  background-color: #e0a800;
  border-color: #d39e00;
}

.btn-warning:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

.mb-3 {
  margin-bottom: 1rem;
}

/* Recovery Modal Styles */
.recovery-workflow {
  padding: 20px 0;
}

.recovery-section-group {
  margin-bottom: 30px;
}

.step-group-title {
  font-size: 20px;
  font-weight: bold;
  color: var(--viva-dark);
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid var(--viva-green);
}

.recovery-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f8f9fa;
  border-left: 4px solid var(--viva-green);
  border-radius: 4px;
  transition: all 0.3s ease;
}

.recovery-substep {
  margin-bottom: 20px;
  margin-left: 20px;
}

.recovery-section h4 {
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--viva-dark);
  font-weight: bold;
}

/* Step state styling */
.recovery-section.step-completed {
  background-color: #e8f5e9;
  border-left-color: #4caf50;
  opacity: 1;
}

.recovery-section.step-active {
  background-color: #f8f9fa;
  border-left-color: var(--viva-green);
  opacity: 1;
}

.recovery-section.step-disabled {
  background-color: #f5f5f5;
  border-left-color: #ccc;
  opacity: 0.6;
}

.recovery-section.step-disabled h4,
.recovery-section.step-disabled p,
.recovery-section.step-disabled label {
  color: #999;
}

.download-links {
  margin-top: 15px;
}

.audio-list {
  background-color: white;
  padding: 15px;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.audio-list ul {
  list-style: none;
  padding: 0;
  margin: 10px 0 0 0;
}

.audio-list li {
  padding: 5px 0;
}

.video-upload-section {
  margin-top: 15px;
}

.video-upload-section label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.questions-table {
  width: 100%;
  margin-top: 15px;
  border-collapse: collapse;
}

.questions-table th,
.questions-table td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
}

.questions-table th {
  background-color: var(--viva-green);
  color: white;
  font-weight: bold;
}

.questions-table input {
  width: 100%;
}

.warning-box {
  background-color: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 6px;
  padding: 20px;
  margin: 15px 0;
}

.warning-box p {
  margin: 5px 0;
}

.btn-lg {
  font-size: 18px;
  padding: 12px 24px;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
  border-color: #dc3545;
}

.btn-danger:hover {
  background-color: #c82333;
  border-color: #bd2130;
}

.btn-danger:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.text-success {
  color: #28a745;
}

.text-warning {
  color: #ffc107;
}

.text-danger {
  color: #dc3545;
}

.text-muted {
  opacity: 0.6;
}

/* Accommodation Toggle Styles */
.accommodation-toggles {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.accommodation-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 2px solid #6c757d;
  background-color: #f8f9fa;
  color: #495057;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.accommodation-btn:hover:not(:disabled) {
  background-color: #e2e6ea;
  border-color: #5a6268;
}

.accommodation-btn.active {
  background-color: var(--viva-green);
  border-color: var(--viva-green);
  color: white;
}

.accommodation-btn.active:hover:not(:disabled) {
  background-color: #5a9f5f;
  border-color: #5a9f5f;
}

.accommodation-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
  color: #495057 !important;
  border-color: #adb5bd !important;
}

.accommodation-btn i {
  font-size: 14px;
}

/* Search Section */
.search-section {
  max-width: 600px;
  margin: 20px auto 20px auto;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  box-sizing: border-box;
}

.search-section h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
  font-size: 18px;
}

.search-input-group {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.search-input {
  width: 400px;
  max-width: 100%;
  height: 40px;
  font-size: 16px;
}

.search-button {
  white-space: nowrap;
  height: 40px;
}

.search-message {
  margin-top: 10px;
  padding: 10px;
  border-radius: 4px;
}

.search-message.success {
  background: #dff0d8;
  color: #3c763d;
  border: 1px solid #d6e9c6;
}

.search-message.warning {
  background: #fcf8e3;
  color: #8a6d3b;
  border: 1px solid #faebcc;
}

.search-message.error {
  background: #f2dede;
  color: #a94442;
  border: 1px solid #ebccd1;
}

/* Viva Details Modal (Orange Theme) */
.viva-details-modal {
  border: 3px solid #ff9800;
}

.viva-details-header {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  border-bottom: 3px solid #f57c00;
}

.viva-details-header h2 {
  color: white;
}

.viva-details-header .close {
  color: white;
  opacity: 0.9;
}

.viva-details-header .close:hover {
  opacity: 1;
}

.no-exam-message {
  text-align: center;
  padding: 40px 20px;
}

.no-exam-message .alert {
  display: inline-block;
  text-align: left;
  max-width: 600px;
}

.no-exam-message h4 {
  margin-top: 0;
  margin-bottom: 15px;
}

.no-exam-message p {
  margin-bottom: 10px;
}

.no-exam-message .btn {
  margin-top: 15px;
}

/* Search Results List Styles */
.search-results-list {
  margin: 20px 0;
}

.search-result-item {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.search-result-item:hover {
  background-color: #f5f5f5;
  border-color: var(--viva-green);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.result-item-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.result-item-id {
  font-size: 16px;
  color: var(--viva-blue);
}

.result-item-info .badge {
  margin-left: 5px;
}

.result-item-details {
  font-size: 14px;
  color: #666;
}

.result-item-details > div {
  margin-bottom: 5px;
}

.search-results-actions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ddd;
  text-align: center;
}

.badge-primary {
  background-color: #337ab7;
}

.badge-info {
  background-color: #5bc0de;
}

.badge-success {
  background-color: #5cb85c;
}

.badge-warning {
  background-color: #f0ad4e;
}

.badge-default {
  background-color: #999;
}
</style>
